<script setup>
import {computed, ref, watch} from 'vue';
import {formatExplainers, explainerSlides} from '~/utils/formatExplainers';

// Step-by-step "how it works" carousel for one competition format. Slides come
// from utils/formatExplainers; `admin` appends the organiser-only steps (they
// get a small "Organisers" chip so the audience shift is obvious). Renders the
// same in a TlDialog or inline on the /formats marketing pages.
const props = defineProps({
  // 'league' | 'americano' | 'ladder' | 'tournament' | 'meet'
  format: {type: String, required: true},
  // 'players' (member steps) | 'organisers' (admin steps only) | 'all'
  audience: {type: String, default: 'players'},
  // false = drop the entry-fee / billing slides (free competition, or a club
  // without payments enabled). Marketing pages leave it on.
  payments: {type: Boolean, default: true},
});

const format = computed(() => formatExplainers[props.format]);
const slides = computed(() => explainerSlides(props.format, props.audience, {payments: props.payments}));

const step = ref(0);
watch(() => [props.format, props.audience], () => (step.value = 0));

const atStart = computed(() => step.value <= 0);
const atEnd = computed(() => step.value >= slides.value.length - 1);
const next = () => {
  if (!atEnd.value) step.value++;
};
const prev = () => {
  if (!atStart.value) step.value--;
};
</script>

<template>
  <div v-if="format" class="fx" :style="{'--fx-tint': format.tint, '--fx-ink': format.ink}">
    <v-window v-model="step" class="fx-window" :touch="true">
      <v-window-item v-for="(slide, i) in slides" :key="slide.title" :value="i">
        <div class="fx-slide">
          <div class="fx-icon">
            <v-icon :icon="slide.icon" size="34"/>
          </div>
          <div v-if="slide.admin" class="fx-audience">
            <v-icon size="12" icon="mdi-shield-account-outline"/>
            Organisers
          </div>
          <h3 class="fx-title">{{ slide.title }}</h3>
          <p class="fx-blurb">{{ slide.blurb }}</p>
        </div>
      </v-window-item>
    </v-window>

    <div class="fx-nav">
      <button type="button" class="fx-arrow" :disabled="atStart" aria-label="Previous step" @click="prev">
        <v-icon size="18" icon="mdi-chevron-left"/>
      </button>
      <div class="fx-dots" role="tablist" aria-label="Steps">
        <button
            v-for="(slide, i) in slides"
            :key="'dot-' + i"
            type="button"
            class="fx-dot"
            :class="{'fx-dot--on': step === i, 'fx-dot--admin': slide.admin}"
            :aria-label="`Step ${i + 1}: ${slide.title}`"
            @click="step = i"
        />
      </div>
      <button type="button" class="fx-arrow" :disabled="atEnd" aria-label="Next step" @click="next">
        <v-icon size="18" icon="mdi-chevron-right"/>
      </button>
    </div>
    <div class="fx-count">Step {{ step + 1 }} of {{ slides.length }}</div>
  </div>
</template>

<style scoped lang="scss">
.fx {
  text-align: center;
}

.fx-window {
  /* Slides vary a line or two in length — a fixed min height stops the dialog
     jumping around as you step through. */
  min-height: 232px;
}

.fx-slide {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 18px 22px 8px;
}

.fx-icon {
  width: 72px;
  height: 72px;
  border-radius: 22px;
  display: grid;
  place-items: center;
  background: var(--fx-tint);
  color: var(--fx-ink);
  margin-bottom: 14px;
}

.fx-audience {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 10.5px;
  font-weight: 700;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  color: var(--fx-ink);
  background: var(--fx-tint);
  border-radius: 999px;
  padding: 3px 10px;
  margin-bottom: 8px;
}

.fx-title {
  font-size: 18px;
  font-weight: 800;
  color: rgb(var(--v-theme-charcoal));
  margin: 0 0 8px;
}

.fx-blurb {
  font-size: 13.5px;
  line-height: 1.65;
  color: rgba(var(--v-theme-charcoal), 0.72);
  max-width: 420px;
  margin: 0;
}

.fx-nav {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 14px;
  margin-top: 6px;
}

.fx-arrow {
  width: 34px;
  height: 34px;
  border-radius: 50%;
  display: grid;
  place-items: center;
  border: 1px solid rgb(var(--v-theme-borderLight));
  background: rgb(var(--v-theme-surface));
  color: rgb(var(--v-theme-charcoal));
  cursor: pointer;
  transition: background 0.15s ease;

  &:hover:not(:disabled) {
    background: var(--fx-tint);
    color: var(--fx-ink);
  }

  &:disabled {
    opacity: 0.35;
    cursor: default;
  }
}

.fx-dots {
  display: flex;
  align-items: center;
  gap: 6px;
}

.fx-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  border: none;
  padding: 0;
  cursor: pointer;
  background: rgb(var(--v-theme-borderLight));
  transition: transform 0.15s ease, background 0.15s ease;

  /* organiser steps get a hollow dot so the audience split reads at a glance */
  &--admin {
    background: transparent;
    box-shadow: inset 0 0 0 1.5px var(--fx-ink);
    opacity: 0.55;
  }

  &--on {
    background: var(--fx-ink);
    opacity: 1;
    transform: scale(1.35);
    box-shadow: none;
  }
}

.fx-count {
  margin-top: 8px;
  font-size: 10.5px;
  font-weight: 700;
  letter-spacing: 0.6px;
  text-transform: uppercase;
  color: rgba(var(--v-theme-charcoal), 0.45);
}
</style>
